import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Card from '../../components/base/Card';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [contactId, setContactId] = useState<string | null>(null);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleShowConfirmation = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      alert('すべての必須項目を入力してください');
      return;
    }

    if (formData.message.length > 500) {
      alert('メッセージは500文字以内で入力してください');
      return;
    }

    setShowConfirmation(true);
  };

  const handleSubmit = async () => {
    try {
      setStatus('loading');
      
      const response = await fetch('https://xaqgqyeqznumoihnasok.supabase.co/functions/v1/send-contact-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setStatus('success');
        setContactId(result.contactId);
        setFormData({ name: '', email: '', subject: '', message: '' });
        setShowConfirmation(false);
        setTimeout(() => setStatus('idle'), 8000);
      } else {
        setStatus('error');
        console.error('Submission error:', result);
        setTimeout(() => setStatus('idle'), 5000);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setStatus('error');
      setTimeout(() => setStatus('idle'), 5000);
    }
  };

  const handleBackToEdit = () => {
    setShowConfirmation(false);
  };

  const handleViewMessages = () => {
    window.REACT_APP_NAVIGATE(`/contact-messages?email=${encodeURIComponent(formData.email)}`);
  };

  return (
    <div className="min-h-screen bg-gray-900">
      <Header />
      
      <main className="py-20">
        {/* Hero Section */}
        <section className="py-16 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Get in Touch
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Have questions about our services? We're here to help. Reach out to our team and we'll get back to you as soon as possible.
            </p>
          </div>
        </section>

        {/* Contact Form and Info Section */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Form */}
              <div>
                <Card className="p-8">
                  {!showConfirmation ? (
                    <>
                      <h2 className="text-2xl font-bold text-white mb-6">Send us a Message</h2>
                      
                      {status === 'success' && (
                        <div className="mb-6 p-4 bg-green-900/30 border border-green-700 rounded-lg">
                          <div className="flex items-center space-x-2">
                            <i className="ri-check-line text-green-400 text-xl"></i>
                            <p className="text-green-400 font-medium">Thank you for contacting us!</p>
                          </div>
                          <p className="text-sm text-green-300 mt-3">
                            We've sent you a confirmation email. We'll get back to you as soon as possible.
                          </p>
                        </div>
                      )}

                      {status === 'error' && (
                        <div className="mb-6 p-4 bg-red-900/30 border border-red-700 rounded-lg">
                          <div className="flex items-center space-x-2">
                            <i className="ri-error-warning-line text-red-400 text-xl"></i>
                            <p className="text-red-400 font-medium">Failed to send. Please try again.</p>
                          </div>
                        </div>
                      )}

                      <form id="contact-form" onSubmit={handleShowConfirmation} className="space-y-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            className="w-full bg-gray-700 text-white rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                            placeholder="Your full name"
                            required
                            disabled={status === 'loading'}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Email <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            className="w-full bg-gray-700 text-white rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                            placeholder="your@email.com"
                            required
                            disabled={status === 'loading'}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Subject <span className="text-red-500">*</span>
                          </label>
                          <select
                            name="subject"
                            value={formData.subject}
                            onChange={(e) => handleInputChange('subject', e.target.value)}
                            className="w-full bg-gray-700 text-white rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-teal-500 text-sm cursor-pointer pr-8"
                            required
                            disabled={status === 'loading'}
                          >
                            <option value="">Select a subject</option>
                            <option value="General Inquiry">General Inquiry</option>
                            <option value="Technical Support">Technical Support</option>
                            <option value="Account Issues">Account Issues</option>
                            <option value="Partnership">Partnership</option>
                            <option value="Other">Other</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Message <span className="text-red-500">*</span>
                          </label>
                          <textarea
                            name="message"
                            value={formData.message}
                            onChange={(e) => handleInputChange('message', e.target.value)}
                            maxLength={500}
                            rows={6}
                            className="w-full bg-gray-700 text-white rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-teal-500 text-sm resize-none"
                            placeholder="How can we help you?"
                            required
                            disabled={status === 'loading'}
                          ></textarea>
                          <p className="text-xs text-gray-400 mt-2">
                            {formData.message.length}/500 characters
                          </p>
                        </div>

                        <button
                          type="submit"
                          disabled={status === 'loading'}
                          className="w-full bg-teal-600 hover:bg-teal-700 disabled:bg-gray-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                        >
                          {status === 'loading' ? (
                            <>
                              <i className="ri-loader-4-line animate-spin"></i>
                              Sending...
                            </>
                          ) : (
                            'Send Message'
                          )}
                        </button>
                      </form>
                    </>
                  ) : (
                    <>
                      <div className="text-center mb-8">
                        <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                          <i className="ri-mail-check-line text-3xl text-white"></i>
                        </div>
                        <h2 className="text-2xl font-bold text-white mb-2">Confirm Your Message</h2>
                        <p className="text-gray-400">Please review your message before sending</p>
                      </div>

                      <Card className="p-6 bg-gray-800 mb-6">
                        <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                          <i className="ri-file-list-3-line mr-2"></i>
                          Message Details
                        </h3>
                        <div className="space-y-3">
                          <div className="flex justify-between py-2 border-b border-gray-700">
                            <span className="text-gray-400">Name</span>
                            <span className="text-white font-medium">{formData.name}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-gray-700">
                            <span className="text-gray-400">Email</span>
                            <span className="text-white font-medium">{formData.email}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-gray-700">
                            <span className="text-gray-400">Subject</span>
                            <span className="text-white font-medium">{formData.subject}</span>
                          </div>
                          <div className="py-2">
                            <span className="text-gray-400 block mb-2">Message</span>
                            <p className="text-white bg-gray-700 p-3 rounded-lg text-sm whitespace-pre-wrap break-words">
                              {formData.message}
                            </p>
                          </div>
                        </div>
                      </Card>

                      <div className="bg-blue-900/30 border border-blue-700 rounded-lg p-4 mb-6">
                        <div className="flex items-start space-x-3">
                          <i className="ri-information-line text-blue-400 text-xl mt-0.5"></i>
                          <div>
                            <h4 className="text-blue-400 font-medium text-sm mb-1">Note</h4>
                            <p className="text-gray-300 text-xs">We typically respond within 24 hours during business days.</p>
                          </div>
                        </div>
                      </div>

                      <div className="flex space-x-2 md:space-x-4">
                        <button
                          type="button"
                          onClick={handleBackToEdit}
                          disabled={status === 'loading'}
                          className="flex-1 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-600 text-white font-semibold py-2 px-3 md:py-3 md:px-6 rounded-lg transition-colors whitespace-nowrap cursor-pointer text-sm md:text-base"
                        >
                          <i className="ri-arrow-left-line mr-1 md:mr-2"></i>
                          Back to Edit
                        </button>
                        <button
                          type="button"
                          onClick={handleSubmit}
                          disabled={status === 'loading'}
                          className="flex-1 bg-teal-600 hover:bg-teal-700 disabled:bg-gray-600 text-white font-semibold py-2 px-3 md:py-3 md:px-6 rounded-lg transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 text-sm md:text-base"
                        >
                          {status === 'loading' ? (
                            <>
                              <i className="ri-loader-4-line animate-spin"></i>
                              Sending...
                            </>
                          ) : (
                            <>
                              <i className="ri-send-plane-fill"></i>
                              Confirm & Send
                            </>
                          )}
                        </button>
                      </div>
                    </>
                  )}
                </Card>
              </div>

              {/* Contact Information */}
              <div className="space-y-8">
                <div>
                  <h2 className="text-2xl font-bold mb-6 text-teal-400">Get in Touch</h2>
                  <p className="text-gray-300 mb-8">
                    We're here to help you with any questions or concerns. Reach out to us through any of the following channels.
                  </p>
                </div>

                {/* Contact Cards */}
                <div className="space-y-6">
                  {/* Email */}
                  <div className="bg-gray-800 rounded-xl p-6 flex items-start space-x-4">
                    <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center">
                      <i className="ri-mail-line text-xl text-white"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2 text-teal-400">Email</h3>
                      <p className="text-gray-300 mb-1">contact us</p>
                      <p className="text-sm text-gray-400">We'll respond within 24 hours</p>
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="bg-gray-800 rounded-xl p-6 flex items-start space-x-4">
                    <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center">
                      <i className="ri-phone-line text-xl text-white"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2 text-teal-400">Telephone</h3>
                      <p className="text-gray-300 mb-1">+63 2 8376 9134</p>
                      <p className="text-sm text-gray-400">Monday - Friday, 9AM - 6PM (PHT)</p>
                    </div>
                  </div>

                  {/* Physical Address */}
                  <div className="bg-gray-800 rounded-xl p-6 flex items-start space-x-4">
                    <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center">
                      <i className="ri-map-pin-line text-xl text-white"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2 text-teal-400">Physical Address</h3>
                      <p className="text-gray-300 mb-1">BLK 18 Lot 2 Sampaguita Corner St.</p>
                      <p className="text-gray-300 mb-1">Brgy San Juan Taytay, Rizal</p>
                      <p className="text-gray-300">Philippines</p>
                    </div>
                  </div>

                  {/* Registered Address */}
                  <div className="bg-gray-800 rounded-xl p-6 flex items-start space-x-4">
                    <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center">
                      <i className="ri-building-line text-xl text-white"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2 text-teal-400">Registered Address</h3>
                      <p className="text-gray-300 mb-1">Bonovo Road, Fomboni</p>
                      <p className="text-gray-300 mb-1">Island of Mohéli</p>
                      <p className="text-gray-300">Comoros Union</p>
                    </div>
                  </div>
                </div>

                {/* Business Hours */}
                <div className="bg-gray-800 rounded-xl p-6">
                  <h3 className="font-semibold mb-4 text-teal-400">Business Hours</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-300">Monday - Friday</span>
                      <span className="text-white">9:00 AM - 6:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Saturday</span>
                      <span className="text-white">10:00 AM - 4:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Sunday</span>
                      <span className="text-white">Closed</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-3">
                      * All times are in Philippine Time (PHT)
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-gray-800">
          <div className="container mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4 text-teal-400">Frequently Asked Questions</h2>
              <p className="text-gray-300">Quick answers to common questions</p>
            </div>
            
            <div className="max-w-4xl mx-auto space-y-6">
              <div className="bg-gray-900 rounded-xl p-6">
                <h3 className="font-semibold mb-3 text-teal-400">How long does it take to process a transaction?</h3>
                <p className="text-gray-300">Most transactions are processed instantly. Bank transfers may take 1-3 business days depending on your bank.</p>
              </div>
              
              <div className="bg-gray-900 rounded-xl p-6">
                <h3 className="font-semibold mb-3 text-teal-400">What are your trading fees?</h3>
                <p className="text-gray-300">
                  Our fees range from 0.5% to 5.0% depending on the transaction amount and payment method. All fees are clearly displayed before you confirm your transaction.
                </p>
              </div>
              
              <div className="bg-gray-900 rounded-xl p-6">
                <h3 className="font-semibold mb-3 text-teal-400">Is my personal information secure?</h3>
                <p className="text-gray-300">Yes, we use industry-standard encryption and security measures to protect your personal and financial information. We never share your data with third parties.</p>
              </div>
              
              <div className="bg-gray-900 rounded-xl p-6">
                <h3 className="font-semibold mb-3 text-teal-400">What cryptocurrencies do you support?</h3>
                <p className="text-gray-300">Currently, we support USDT trading. We're continuously working to add more cryptocurrencies to our platform.</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
